import './index.css'

import { MdArrowDropDown as ArrowDropDown } from '@react-icons/all-files/md/MdArrowDropDown'
import { MdArrowDropUp as ArrowDropUp } from '@react-icons/all-files/md/MdArrowDropUp'
import cn from 'clsx'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import React, { useState } from 'react'

import { ScrollArea } from 'components/ds'
import Skeleton from 'components/ui/skeleton'

import DataTableLink from './link'

type Sorting = {
  col: number
  dir: -1 | 1
}

type Props<T = Record<string, unknown>> = {
  appearance?: 'bordered' | 'plain' | 'striped'
  columns: Column<T>[]
  data: T[]
  defaultSort?: Sorting
  emptyState?: React.ReactNode
  emptyStateCompact?: boolean
  loading?: boolean
  rows?: number | null | undefined
  scrollable?: boolean
}
export type Column<T = Record<string, unknown>> = {
  id?: string
  name?: React.ReactNode
  // Probably temporary property
  caption?: boolean | null | undefined
  contentType?: 'text' | 'button'
  render?: ((value: unknown) => React.ReactNode) | null | undefined
  headerRender?: ((name: React.ReactNode) => React.ReactNode) | null | undefined
  type?: 'string' | 'numeric' | 'date'
  sortable?: boolean | string | ((row: T) => unknown)
  toSortable?: (row: T) => unknown
  width?: 'small' | 'medium' | 'large' | 'content'
}

const improveSorting = (v: unknown) => {
  if (typeof v === 'string') return v.toLowerCase()
  else return v
}

// TODO: Implement better skeleton loading
/**
 * A sortable data table component with customizable appearance, loading states, and empty states
 * @param appearance - Visual style of the table ('bordered', 'plain', or 'striped') - defaults to 'striped'
 * @param columns - Array of column definitions with names, types, and render functions
 * @param data - Array of data objects to display in the table
 * @param defaultSort - Optional default sorting configuration with column and direction
 * @param emptyState - Custom content to display when no data is available
 * @param emptyStateCompact - Whether to use compact styling for empty state - defaults to false
 * @param loading - Whether the table is in loading state - defaults to false
 * @param rows - Optional fixed number of rows to display (for skeleton loading)
 * @param scrollable - Whether to make the table scrollable - defaults to false
 * @returns A div or ScrollArea containing the data table with sorting and rendering capabilities
 */
const DataTable = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  appearance = 'striped',
  columns,
  data,
  defaultSort,
  emptyState,
  emptyStateCompact,
  loading,
  rows,
  scrollable,
}: Props<T>): React.ReactNode => {
  const [sorting, setSorting] = useState<Sorting>(defaultSort) // no sort by default

  const handleColumnClick = (columnIdx: number) => {
    setSorting(sorting => ({
      col: columnIdx,
      dir: (columnIdx === sorting?.col ? -sorting?.dir : 1) as -1 | 1,
    }))
  }

  let renderingData = data

  if (sorting) {
    const column = columns[sorting.col]

    const toSortable = column.toSortable || ((r: T) => get(r, column.id))

    const iter = (x: T) => improveSorting(toSortable(x))

    const isReverseSort = column.type === 'numeric' || column.type === 'date'
    renderingData = orderBy(
      renderingData,
      iter,
      sorting.dir === (isReverseSort ? -1 : 1) ? 'asc' : 'desc',
    )
  }

  let renderingColumns = columns.map(column => ({
    ...column,
    sortable: column.toSortable || column.id,
  }))
  const main =
    !loading && isEmpty(data) ? (
      <div
        className={cn('DataTable_Empty', {
          'DataTable_Empty--compact': emptyStateCompact,
        })}
      >
        {emptyState ?? 'Sorry! There’s no data available.'}
      </div>
    ) : (
      <table className="DataTable_Container">
        {shouldRenderHead(columns) && (
          <thead className="DataTable_Header">
            <tr className="DataTable_Header_Row">
              {loading
                ? renderHead(
                    [
                      {
                        name: 'Loading...',
                      },
                    ],
                    sorting,
                    handleColumnClick,
                  )
                : renderHead(renderingColumns, sorting, handleColumnClick)}
            </tr>
          </thead>
        )}

        <tbody>
          {loading
            ? renderBody(
                [
                  // TODO: Optimize loading for striped
                  appearance === 'plain'
                    ? {
                        render: () => <Skeleton lines={1} />,
                      }
                    : {},
                ],
                [...new Array(rows)],
              )
            : renderBody(columns, renderingData, rows)}
        </tbody>
      </table>
    )
  const className = cn(`DataTable DataTable--${appearance}`, {
    'DataTable--scrollable': scrollable,
  })

  if (scrollable) {
    return (
      <ScrollArea type="auto" scrollbars="both" className={className}>
        {main}
      </ScrollArea>
    )
  } else {
    return <div className={className}>{main}</div>
  }
}

const renderSortButton = sorting =>
  sorting.dir === -1 ? <ArrowDropUp size={16} /> : <ArrowDropDown size={16} />

const shouldRenderHead = columns => columns.find(column => column.name)

const renderHead = <T extends Record<string, unknown>>(
  columns: Array<Column<T>>,
  sorting: Sorting | undefined,
  onColumnClick: (index: number) => void,
) =>
  columns.map((item, index) => (
    <th
      key={index}
      className={cn('DataTable_Header_Cell', {
        'DataTable_Cell--text': item.width !== 'content',
        'DataTable_Cell--Sortable': item.sortable,
        'DataTable_Cell--Caption': item.caption === undefined,
      })}
      onClick={() => {
        if (item.sortable) {
          onColumnClick(index)
        }
      }}
    >
      <span>
        {item.headerRender ? item.headerRender(item.name) : item.name}
      </span>
      {sorting && index === sorting.col && renderSortButton(sorting)}
    </th>
  ))

const renderBody = <T extends Record<string, unknown>>(
  columns: Column<T>[],
  data: T[],
  rows: number = data.length,
) =>
  [...new Array(Math.max(rows, data.length))].map((row, rowIndex) => {
    let rowData = data[rowIndex]
    let key =
      (rowData as Record<string, unknown> & { key?: React.Key })?.key ||
      rowIndex
    return (
      <tr key={key} className="DataTable_Row">
        {rowIndex < data.length ? (
          columns.map((column, colIndex) => {
            const value = column.id ? get(rowData, column.id) : rowData
            const rendered = column.render
              ? column.render(value)
              : get(rowData, column.id)
            return (
              <td
                key={colIndex}
                className={cn(
                  `DataTable_Cell DataTable_Cell--${
                    column.contentType || 'text'
                  }`,
                )}
                data-width={column.width ?? 'medium'}
              >
                {rendered as React.ReactNode}
              </td>
            )
          })
        ) : (
          <td className="DataTable_Cell" colSpan={columns.length} />
        )}
      </tr>
    )
  })

DataTable.Link = DataTableLink
export default DataTable
